/**
 * Student plans (docs/05 §3, docs/14 B2–B5). The engine runs on the server from the effective policies of
 * the student's circle; clients send the starting point and show the result.
 */
import { z } from "zod";
import { POLICY_KEYS, AmountUnit, PolicyStatus } from "./policies.js";
const surah = z.number().int().min(1).max(114);
const ayah = z.number().int().min(1).max(286);
const date = z.iso.date();
export const PlanPreviewBody = z.object({
    enrollmentId: z.uuid(),
    /** The next ayah to memorize. */
    start: z.object({ surah, ayah }),
    /** The plan's last ayah (default: the end of Al-Baqarah backward, the end of An-Nas forward). */
    end: z.object({ surah, ayah }).optional(),
    /** Overrides the policy's default direction for this student (B3). */
    direction: z.enum(["backward", "forward"]).optional(),
    /** Surahs the student has already memorized in full, for review. */
    memorizedSurahs: z.array(surah).max(114).default([]),
    /**
     * A program of the student's own (owner request 2026-10-26): these values replace the circle's policies
     * for this student only; anything left out follows the circle. null: review of that kind is turned off.
     */
    custom: z
        .object({
        unit: POLICY_KEYS["memorization.unit"].schema.optional(),
        dailyAmount: POLICY_KEYS["memorization.dailyAmount"].schema.optional(),
        near: POLICY_KEYS["review.near"].schema.nullable().optional(),
        far: POLICY_KEYS["review.far"].schema.nullable().optional(),
        studyDays: POLICY_KEYS["schedule.studyDays"].schema.optional(),
    })
        .optional(),
    from: date,
    days: z.number().int().min(1).max(60).default(14),
});
export const PlanRange = z.object({ start: z.number(), end: z.number(), label: z.string() });
export const PlanPreviewItem = z.object({
    date: z.string(),
    kind: z.enum(["new", "near_review", "far_review"]),
    ranges: z.array(PlanRange),
    amount: z.number(),
    unit: AmountUnit,
});
export const PlanPolicyUsed = z.object({
    key: z.string(),
    value: z.unknown(),
    status: PolicyStatus,
    source: z.string(),
});
export const PlanPreviewResponse = z.object({
    student: z.object({ id: z.uuid(), name: z.string() }),
    circle: z.object({ id: z.uuid(), name: z.string() }),
    direction: z.enum(["backward", "forward"]),
    /** The policy values the plan was built from; any provisional one makes the plan provisional. */
    policies: z.array(PlanPolicyUsed),
    provisional: z.boolean(),
    items: z.array(PlanPreviewItem),
    /** Where the next window starts (null: the plan's range is complete). */
    nextAyah: z.object({ surah: z.number(), ayah: z.number(), label: z.string() }).nullable(),
});
export const SurahsResponse = z.object({
    surahs: z.array(z.object({ id: z.number(), nameAr: z.string(), ayahCount: z.number() })),
});
// ---------- stored plans (Phase 4.3) ----------
/** «withdrawn»: a teacher took back their proposal before approval (B12-a). */
export const PlanStatus = z.enum(["draft", "approved", "superseded", "withdrawn"]);
/** «partial» (B8-a): part of the item was recited; its ranges and amount are that part. */
export const PlanItemStatus = z.enum(["pending", "passed", "repeat", "missed", "skipped", "partial"]);
/**
 * Same input as the preview, without a duration: a plan runs until the student finishes its range, and the
 * server keeps its next days generated (owner's decision, 2026-10-13).
 */
export const CreatePlanBody = PlanPreviewBody.omit({ days: true });
export const StoredPlanItem = PlanPreviewItem.extend({
    id: z.uuid(),
    status: PlanItemStatus,
    /** Part of the amount carried over from missed days (B9 carry_over). */
    carried: z.number().nullable(),
    /** For a partial item: what was planned (its `ranges`/`amount` are the recited part). */
    planned: z
        .object({ ranges: z.array(PlanRange), amount: z.number() })
        .nullable()
        .optional(),
    /** A review item the student marked as reviewed on their own (owner decision 2026-10-26). */
    selfReviewedAt: z.string().nullable().optional(),
});
export const SelfReviewBody = z.object({ done: z.boolean() });
export const PlanSummary = z.object({
    id: z.uuid(),
    status: PlanStatus,
    direction: z.enum(["backward", "forward"]),
    start: z.string(),
    end: z.string(),
    provisional: z.boolean(),
    generatedFrom: z.string(),
    generatedUntil: z.string(),
    /** Set while an absence has paused the plan (B9 pause). */
    pausedSince: z.string().nullable(),
    createdAt: z.string(),
    approvedAt: z.string().nullable(),
    /** The teacher who proposed the plan (B12-a); null when a supervisor made it. */
    proposedBy: z.object({ id: z.uuid(), name: z.string() }).nullable(),
});
export const PlanDetail = PlanSummary.extend({
    enrollmentId: z.uuid(),
    student: z.object({ id: z.uuid(), name: z.string() }),
    circle: z.object({ id: z.uuid(), name: z.string() }),
    policies: z.array(PlanPolicyUsed),
    items: z.array(StoredPlanItem),
});
export const PlanResponse = z.object({ plan: PlanDetail });
export const PlansResponse = z.object({ plans: z.array(PlanSummary) });
export const PlansQuery = z.object({ enrollmentId: z.uuid() });
/** The day is the caller's local date (the client knows its time zone; the server does not guess). */
export const DayQuery = z.object({ date });
/** One student's items for a day (teacher's circle view, and the student's own). */
export const DayPlan = z.object({
    student: z.object({ id: z.uuid(), name: z.string() }),
    circle: z.object({ id: z.uuid(), name: z.string() }),
    planId: z.uuid(),
    provisional: z.boolean(),
    items: z.array(StoredPlanItem),
    /** The circle's `absence.carryOverAlert`: a carried amount above it is flagged to the teacher. */
    carryOverAlert: z.number().nullable(),
});
export const CircleDayResponse = z.object({ date: z.string(), students: z.array(DayPlan) });
/** A student may be enrolled in more than one circle: one entry per approved plan with items that day. */
export const MyDayResponse = z.object({ date: z.string(), plans: z.array(DayPlan) });
/** Resumes a plan an absence paused (B9 pause), from the given day. */
export const ResumePlanBody = z.object({ from: date });
/** The student's own items over a span of days (the «وردي» screen): at most 31 days. */
export const MyPlanDaysQuery = z
    .object({ from: date, to: date })
    .refine((q) => q.from <= q.to, { message: "بداية الفترة بعد نهايتها" });
export const MyPlanDaysResponse = z.object({
    from: z.string(),
    to: z.string(),
    items: z.array(StoredPlanItem.extend({ circle: z.object({ id: z.uuid(), name: z.string() }) })),
});
/** Plans across the caller's scope (portal «الخطط»): one row per active enrollment. */
export const PlansOverviewRow = z.object({
    enrollmentId: z.uuid(),
    student: z.object({ id: z.uuid(), name: z.string() }),
    circle: z.object({ id: z.uuid(), name: z.string() }),
    /** The plan in force, if one was approved. */
    approved: z
        .object({ id: z.uuid(), approvedAt: z.string().nullable(), pausedSince: z.string().nullable() })
        .nullable(),
    /** A draft newer than the approved plan, waiting for approval. */
    draftId: z.uuid().nullable(),
    /** The teacher who proposed that draft (B12-a), if a teacher did. */
    draftProposedBy: z.string().nullable(),
    /** Items of the approved plan due before today and not done. */
    overdue: z.number(),
});
export const PlansOverviewResponse = z.object({ today: z.string(), rows: z.array(PlansOverviewRow) });
//# sourceMappingURL=plans.js.map